import type { RewardOption } from '@/components/admin/mail/reward-options';
import { MailRewardPicker } from '@/components/admin/MailRewardPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { TierDraft, ValidationIssue } from './payment-event.model';

interface Props {
  tiers: TierDraft[];
  options: RewardOption[];
  issues: ValidationIssue[];
  disabled?: boolean;
  onChange: (next: TierDraft[]) => void;
}

export function TierEditor({ tiers, options, issues, disabled, onChange }: Props) {
  const patch = (i: number, next: Partial<TierDraft>) =>
    onChange(tiers.map((t, idx) => (idx === i ? { ...t, ...next } : t)));

  const messagesFor = (prefix: string) =>
    issues.filter((x) => x.field === prefix || x.field.startsWith(`${prefix}.`));

  return (
    <div className="flex flex-col gap-3">
      {tiers.map((tier, i) => {
        const tierIssues = messagesFor(`tier.${i}`);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: 티어는 위치로 식별한다
          <div key={i} className="flex flex-col gap-2 rounded-md border border-border p-3">
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`tier-threshold-${i}`}>티어 {i + 1} VX 임계값</Label>
                <Input
                  id={`tier-threshold-${i}`}
                  type="number"
                  min={1}
                  className="w-40"
                  disabled={disabled}
                  value={tier.threshold_vx}
                  onChange={(e) => patch(i, { threshold_vx: Number(e.target.value) })}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`티어 ${i + 1} 삭제`}
                disabled={disabled}
                onClick={() => onChange(tiers.filter((_, idx) => idx !== i))}
              >
                삭제
              </Button>
            </div>

            <MailRewardPicker
              allowCustomKey
              options={options}
              rows={tier.rewards}
              disabled={disabled}
              onChange={(rewards) => patch(i, { rewards })}
            />

            {tierIssues.map((x) => (
              <p key={x.field + x.message} className="text-xs text-rose-600 dark:text-rose-400">
                {x.message}
              </p>
            ))}
          </div>
        );
      })}

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...tiers, { threshold_vx: 0, rewards: [] }])}
        >
          티어 추가
        </Button>
      </div>
    </div>
  );
}
